import { useMemo, useState } from 'react';
import type { DashboardPeriodMode, VisitsAnalyticsQuery, VisitsSourceCard } from '@troia-v3/types';

import { Breadcrumb, PageToolbar, PeriodPicker, Select, Tabs } from '@/shared/components/ui';
import type { PeriodPickerValue } from '@/shared/components/ui';
import { DashCard, MetricTile, formatNumber, formatPct } from '@/shared/components/ui/dashboard';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';

import { ConnectViewSourceDrawer } from '../components/ConnectViewSourceDrawer';
import { DayHourConcentrationCard } from '../components/DayHourConcentrationCard';
import { LeadFiltersButton, type LeadFilters } from '../components/LeadFiltersButton';
import { VisitsBreakdownCard } from '../components/VisitsBreakdownCard';
import { VisitsEntriesCard } from '../components/VisitsEntriesCard';
import { VisitsHourCard } from '../components/VisitsHourCard';
import { VisitsSeriesCard } from '../components/VisitsSeriesCard';
import { VisitsSourcesCard } from '../components/VisitsSourcesCard';
import { useVisitsAnalytics, useVisitsEntries } from '../hooks/useVisitsAnalytics';
import { ALL_UNITS, buildUnitFilterSelect, unitFilterToQuery } from '@/shared/utils/unitFilter';

interface CustomRange {
  start: string;
  end: string;
}

const formatDay = (iso: string): string => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

export const VisitsAnalyticsPage = () => {
  const now = new Date();
  const [period, setPeriod] = useState<DashboardPeriodMode>('month');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [page, setPage] = useState(1);
  const [connectOpen, setConnectOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<VisitsSourceCard | null>(null);
  const [tab, setTab] = useState<'overview' | 'entries'>('overview');
  const [leadFilters, setLeadFilters] = useState<LeadFilters>({});
  // Tudo, um estado inteiro (`state:SC`) ou uma unidade — ver `utils/unitFilter`.
  const [unitFilter, setUnitFilter] = useState<string>(ALL_UNITS);
  // < sm (640px), o mesmo corte das classes `sm:` usadas na barra.
  const isMobile = useIsMobile(639);

  // Lista TODAS as unidades ativas, e não `availableFilters.units` do payload:
  // aquela sai do mesmo recorte da tela, então com uma unidade escolhida ela
  // encolheria para um item só e não haveria como trocar de unidade.
  const { data: units } = useUnitsDropdown();
  const unitSelect = useMemo(() => buildUnitFilterSelect(units ?? []), [units]);

  const query = useMemo<VisitsAnalyticsQuery>(() => {
    // A unidade (ou o estado) vale para a tela INTEIRA — visitas, leads,
    // vendas e extrato —, ao contrário dos recortes de lead logo abaixo.
    const filters = {
      ...unitFilterToQuery(unitFilter),
      ...(leadFilters.origin ? { leadOrigin: leadFilters.origin } : {}),
      ...(leadFilters.status ? { leadStatus: leadFilters.status } : {}),
    };
    if (period === 'custom' && customRange) {
      return { period, startDate: customRange.start, endDate: customRange.end, ...filters };
    }
    if (period === 'month') {
      return { period, year: now.getFullYear(), month: now.getMonth() + 1, ...filters };
    }
    if (period === 'year') return { period, year: now.getFullYear(), ...filters };
    return { period, ...filters };
    // `now` recriado a cada render é intencionalmente ignorado: o período só
    // muda quando o usuário escolhe outro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customRange, leadFilters, unitFilter]);

  const { data, isLoading, isError, error, refetch } = useVisitsAnalytics(query);
  // O extrato só é buscado com a aba aberta — é uma consulta paginada sobre a
  // collection crua, e pagá-la em toda visita à página para um painel que
  // ninguém rolava era o motivo de ele sair da tela inicial.
  const { data: entries, isLoading: entriesLoading } = useVisitsEntries(
    { ...query, page, limit: 25 },
    tab === 'entries',
  );

  const pickerValue: PeriodPickerValue =
    period === 'custom' && customRange
      ? { mode: 'custom', start: customRange.start, end: customRange.end }
      : { mode: period === 'custom' ? 'month' : period };

  const handlePeriodChange = (value: PeriodPickerValue): void => {
    setPage(1);
    if (value.mode === 'custom' && value.start && value.end) {
      setPeriod('custom');
      setCustomRange({ start: value.start, end: value.end });
      return;
    }
    setPeriod(value.mode);
    setCustomRange(null);
  };

  const handleUnitChange = (value: string): void => {
    // O extrato é paginado sobre o recorte: página 7 de "todas" não existe
    // numa unidade só.
    setPage(1);
    setUnitFilter(value);
  };

  // Montados UMA vez e encaixados em um lugar só (direita no desktop, linha
  // própria no celular): duas cópias no DOM duplicariam estado e `data-testid`.
  const controls = (
    <>
      {/* Unidade ou estado — filtra todos os cards, gráficos e o extrato */}
      <div className="flex-shrink-0 w-[200px]">
        <Select
          value={unitFilter}
          onChange={handleUnitChange}
          options={unitSelect.options}
          {...(unitSelect.groups ? { groups: unitSelect.groups } : {})}
          aria-label="Unidade ou estado"
          title="Unidade ou estado"
          style={{ height: 36 }}
          data-testid="analytics-unit"
        />
      </div>
      <LeadFiltersButton value={leadFilters} onChange={setLeadFilters} />
      <PeriodPicker value={pickerValue} onChange={handlePeriodChange} />
    </>
  );

  const coverageLabel = data?.coverage.coverageUntil
    ? `medido até ${new Date(data.coverage.coverageUntil).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })}`
    : null;

  const overview = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 flex flex-col gap-4">
      {isLoading && (
        <div
          className="py-16 text-center text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
          data-testid="analytics-visits-loading"
        >
          Carregando visitas…
        </div>
      )}

      {isError && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
          data-testid="analytics-visits-error"
        >
          Erro ao carregar: {(error as Error)?.message}
        </div>
      )}

      {!isLoading && !isError && data && (
        <>
          {data.sources.length === 0 && data.kpis.totalViews.value === 0 ? (
            <DashCard icon="activity" title="Sem visitas medidas" testId="analytics-visits-empty">
              <div className="py-6 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Quando a contagem das suas unidades começar a chegar, os gráficos aparecem aqui.
              </div>
            </DashCard>
          ) : (
            <>
              <div className="grid grid-cols-2 max-[420px]:grid-cols-1 min-[560px]:grid-cols-3 min-[1180px]:grid-cols-6 gap-3">
                <MetricTile
                  icon="eye"
                  label="Total de visitas"
                  value={formatNumber(data.kpis.totalViews.value)}
                  deltaPct={data.kpis.totalViews.deltaPct}
                  trend={data.kpis.totalViews.trend}
                  testId="analytics-visits-kpi-total"
                />
                <MetricTile
                  icon="bar-chart"
                  label="Média diária"
                  value={formatNumber(data.kpis.dailyAvg.value)}
                  deltaPct={data.kpis.dailyAvg.deltaPct}
                  trend={data.kpis.dailyAvg.trend}
                  testId="analytics-visits-kpi-daily-avg"
                />
                <MetricTile
                  icon="trending-up"
                  label="Dia de pico"
                  value={data.kpis.peakDay ? formatNumber(data.kpis.peakDay.views) : '—'}
                  deltaPct={0}
                  {...(data.kpis.peakDay ? { caption: formatDay(data.kpis.peakDay.date) } : {})}
                  testId="analytics-visits-kpi-peak-day"
                />
                <MetricTile
                  icon="clock"
                  label="Hora de pico"
                  value={data.kpis.peakHour ? `${String(data.kpis.peakHour.hour).padStart(2, '0')}h` : '—'}
                  deltaPct={0}
                  {...(data.kpis.peakHour ? { caption: `${formatNumber(data.kpis.peakHour.views)} visitas` } : {})}
                  testId="analytics-visits-kpi-peak-hour"
                />
                <MetricTile
                  icon="map-pin"
                  label="Cobertura"
                  value={`${data.coverage.unitsReporting} de ${data.coverage.unitsExpected}`}
                  deltaPct={0}
                  caption={
                    data.coverage.unitsReporting < data.coverage.unitsExpected
                      ? 'unidades sem reportar'
                      : 'unidades reportando'
                  }
                  testId="analytics-visits-kpi-coverage"
                />
                {/* `leads` pode faltar por até 5 minutos depois do deploy: o
                    payload sai de um cache que não versiona o formato. */}
                {data.kpis.leads ? (
                  <MetricTile
                    icon="users"
                    label="Leads"
                    value={formatNumber(data.kpis.leads.value)}
                    deltaPct={data.kpis.leads.deltaPct}
                    trend={data.kpis.leads.trend}
                    caption={
                      data.kpis.leads.conversionPct === null
                        ? 'sem visitas para comparar'
                        : `${formatPct(data.kpis.leads.conversionPct)} das visitas`
                    }
                    testId="analytics-visits-kpi-leads"
                  />
                ) : null}
              </div>

              <VisitsSeriesCard series={data.series} granularity={data.granularity} />

              <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-4">
                <VisitsHourCard byHour={data.byHour} peakHour={data.kpis.peakHour?.hour ?? null} />
                <VisitsBreakdownCard
                  icon="map-pin"
                  title="Visitas por unidade"
                  rows={data.byUnit}
                  testIdPrefix="analytics-visits-unit"
                  emptyLabel="Nenhuma unidade reportou no período."
                />
              </div>

              <DayHourConcentrationCard visits={data.heatmap} query={query} />

              <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-4">
                <VisitsBreakdownCard
                  icon="message-circle"
                  title="Visitas por canal"
                  rows={data.byChannel}
                  testIdPrefix="analytics-visits-channel"
                  emptyLabel="Sem dados de canal."
                />
                <VisitsBreakdownCard
                  icon="globe"
                  title="Visitas por origem"
                  rows={data.byOrigin}
                  testIdPrefix="analytics-visits-origin"
                  emptyLabel="Sem dados de origem."
                />
              </div>
            </>
          )}

          {/* Fontes por ÚLTIMO: é configuração, não leitura. Quem abre a tela
              quer o número; de onde ele vem só interessa quando algo parece
              errado — e aí a pessoa rola até o fim de propósito. Quando não há
              nenhuma fonte, o vazio acima já assume o papel de chamada. */}
          <VisitsSourcesCard
            sources={data.sources}
            onChanged={() => void refetch()}
            onConnect={() => { setEditingSource(null); setConnectOpen(true); }}
            onEdit={(source) => { setEditingSource(source); setConnectOpen(true); }}
          />
        </>
      )}
    </div>
  );

  return (
    <div
      style={{ backgroundColor: 'var(--theme-page-background)', minHeight: '100%' }}
      data-testid="analytics-visits-page"
    >
      <div className="sticky top-0 z-20">
        <PageToolbar
          left={
            <>
              {/* `flex-none`: a trilha trunca, então encolhe até zero e o
                  título some quando os controles disputam a mesma linha. */}
              <div className="flex-none">
                <Breadcrumb
                  items={[{ label: 'Dashboards' }, { label: 'Analytics' }]}
                  mobileLegend={`Dashboards · ${data?.period.label ?? '…'}`}
                />
              </div>
              {/* No celular o período vai na legenda acima. */}
              <span className="hidden sm:inline text-xs ml-2" style={{ color: 'var(--theme-muted-text)' }}>
                {data?.period.label ?? 'Carregando período…'}
                {coverageLabel ? ` · ${coverageLabel}` : ''}
              </span>
            </>
          }
          right={
            isMobile ? undefined : (
              <div className="flex items-center gap-2 flex-wrap justify-end">{controls}</div>
            )
          }
          below={
            // No celular os três controles não cabem ao lado do título: descem
            // para uma linha própria, rolável na horizontal.
            isMobile ? (
              <div
                className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                style={{
                  padding: '9px 14px',
                  backgroundColor: 'var(--theme-header-background)',
                  borderBottom: '1px solid var(--theme-header-border)',
                }}
                data-testid="analytics-toolbar-mobile"
              >
                {controls}
              </div>
            ) : undefined
          }
        />
      </div>

      <Tabs
        variant="underline"
        pageLevel
        hideIconsOnMobile
        activeTab={tab}
        onChange={(id) => setTab(id as 'overview' | 'entries')}
        tabs={[
          {
            id: 'overview',
            label: 'Visão geral',
            icon: 'bar-chart',
            'data-testid': 'analytics-tab-overview',
            content: overview,
          },
          {
            id: 'entries',
            label: 'Extrato de visitas',
            icon: 'list',
            'data-testid': 'analytics-tab-entries',
            content: (
              <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
                <VisitsEntriesCard
                  data={entries}
                  isLoading={entriesLoading}
                  page={page}
                  onPageChange={setPage}
                />
              </div>
            ),
          },
        ]}
      />

      <ConnectViewSourceDrawer
        isOpen={connectOpen}
        onClose={() => { setConnectOpen(false); setEditingSource(null); }}
        onConnected={() => void refetch()}
        editing={editingSource}
      />
    </div>
  );
};

VisitsAnalyticsPage.displayName = 'VisitsAnalyticsPage';
